// src/components/Properties/PropertiesPanel/PropertiesPanel.tsx
import React, { useMemo } from 'react';
import { Settings } from 'lucide-react';
import { useUiStateStore } from 'src/stores/uiStateStore';
import { useGroupStore } from 'src/stores/groupStore';
import { ConnectorProperties } from '../ConnectorProperties/ConnectorProperties';
import { NodeProperties } from '../NodeProperties/NodeProperties';
import { RectangleProperties } from '../RectangleProperties/RectangleProperties';
import { TextProperties } from '../TextProperties/TextProperties';
import { GroupProperties } from '../GroupProperties/GroupProperties';
import { GroupableElement } from 'src/features/groups/groupTypes';
import { useScene } from 'src/hooks/useScene';

export const PropertiesPanel: React.FC = () => {
  const itemControls = useUiStateStore((state) => state.itemControls);
  const groupActions = useGroupStore((state) => state.actions);
  const groups = useGroupStore((state) => state.groups);
  const scene = useScene();

  const selectedConnectorId = useMemo(() => {
    if (itemControls?.type === 'CONNECTOR') return itemControls.id;
    if (itemControls?.type !== 'CONNECTOR_ANCHOR') return undefined;
    return scene.connectors.find((connector) =>
      connector.anchors.some((anchor) => anchor.id === itemControls.id)
    )?.id;
  }, [itemControls, scene.connectors]);

  // Check if selected items belong to a group
  const selectedGroup = useMemo(() => {
    if (!itemControls) {
      return null;
    }

    // Get all selected elements
    const selectedElements: GroupableElement[] = [];

    if (itemControls.type === 'MULTI_SELECT') {
      itemControls.items.forEach((item) => {
        if (
          item.type === 'ITEM' ||
          item.type === 'RECTANGLE' ||
          item.type === 'TEXTBOX' ||
          item.type === 'CONNECTOR'
        ) {
          selectedElements.push({
            type: item.type as any,
            id: item.id
          });
        }
      });
    } else if (
      itemControls.type === 'ITEM' ||
      itemControls.type === 'RECTANGLE' ||
      itemControls.type === 'TEXTBOX' ||
      itemControls.type === 'CONNECTOR'
    ) {
      selectedElements.push({
        type: itemControls.type as any,
        id: itemControls.id
      });
    }


    if (selectedElements.length === 0) {
      return null;
    }

    // Find all groups that have at least one selected element
    const groupCounts = new Map<string, number>();

    selectedElements.forEach((el) => {
      const elGroup = groupActions.getGroupForElement(el);
      if (elGroup) {
        groupCounts.set(elGroup.id, (groupCounts.get(elGroup.id) || 0) + 1);
      }
    });

    // If no groups found, return null
    if (groupCounts.size === 0) {
      return null;
    }

    // Find the group with the most selected elements
    let mostSelectedGroup = null;
    let maxCount = 0;

    groupCounts.forEach((count, groupId) => {
      if (count > maxCount) {
        maxCount = count;
        mostSelectedGroup = groups.find(g => g.id === groupId);
      }
    });

    return mostSelectedGroup;
  }, [itemControls, groupActions, groups]);

  // ✅ Show group properties if a group is selected
  if (selectedConnectorId) {
    return (
      <div className="flex h-full flex-col bg-white dark:bg-slate-900">
        <ConnectorProperties id={selectedConnectorId} />
      </div>
    );
  }

  if (selectedGroup) {
    return (
      <div className="flex h-full flex-col bg-white dark:bg-slate-900">
        <GroupProperties groupId={(selectedGroup as any).id} />
      </div>
    );
  }

  // Check what type of item is selected (individual items, not grouped)
  const selectedItemType = itemControls?.type;
  const selectedItemId =
    itemControls && 'id' in itemControls ? itemControls.id : undefined;
  // ✅ Handle connector properties
  // ✅ Handle node/item properties
  if (selectedItemType === 'ITEM' && selectedItemId) {
    return (
      <div className="flex h-full flex-col bg-white dark:bg-slate-900">
        <NodeProperties id={selectedItemId} />
      </div>
    );
  }

  // ✅ Handle rectangle properties
  if (selectedItemType === 'RECTANGLE' && selectedItemId) {
    return (
      <div className="flex h-full flex-col bg-white dark:bg-slate-900">
        <RectangleProperties id={selectedItemId} />
      </div>
    );
  }

  // ✅ Handle textbox properties
  if (selectedItemType === 'TEXTBOX' && selectedItemId) {
    return (
      <div className="flex h-full flex-col bg-white dark:bg-slate-900">
        <TextProperties id={selectedItemId} />
      </div>
    );
  }

  // Handle no selection
  if (!itemControls) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <Settings className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
            No Selection
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Select an item to view its properties
          </p>
        </div>
      </div>
    );
  }

  // Fallback for other item types
  return (
    <div className="flex h-full items-center justify-center">
      <div className="text-center">
        <Settings className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600" />
        <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
          Properties Not Available
        </h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          This item type does not have editable properties yet
        </p>
      </div>
    </div>
  );
};
